import React, { useState, useRef, useEffect } from 'react';
import { api } from '../services/api';
import { ChatMessage } from '../types';
import MarkdownRenderer from './MarkdownRenderer';

interface ChatInterfaceProps {
  imageBase64: string;
  imageMimeType: string;
  initialAnalysis: string;
  restoredMessages?: ChatMessage[]; // History from a saved session
  onMessagesChange: (messages: ChatMessage[]) => void; // Sync state with parent
  className?: string;
  onReAnalyze?: () => void; // Optional callback for re-analyze button
  isReAnalyzing?: boolean; // Loading state for re-analyze
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ 
  imageBase64, 
  imageMimeType, 
  initialAnalysis,
  restoredMessages = [],
  onMessagesChange,
  className = "h-[500px] mt-8",
  onReAnalyze,
  isReAnalyzing = false
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>(restoredMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const contextRef = useRef({
    imageBase64,
    imageMimeType,
    initialAnalysis,
    history: restoredMessages
  });

  // Initialize messages on mount
  useEffect(() => {
    setMessages(restoredMessages);
    contextRef.current.history = restoredMessages;
  }, []); // Empty dependency array relies on key-based remounting

  // Notify parent of message changes
  useEffect(() => {
      onMessagesChange(messages);
  }, [messages, onMessagesChange]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const formatTime = (ts?: number) => {
      if (!ts) return '';
      return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    
    const newMsg: ChatMessage = { role: 'user', text: userMessage, timestamp: Date.now() };
    setMessages(prev => [...prev, newMsg]);
    setIsLoading(true);

    try {
      // Call backend API for chat
      const response = await api.chat(userMessage, {
        imageBase64: contextRef.current.imageBase64,
        imageMimeType: contextRef.current.imageMimeType,
        initialAnalysis: contextRef.current.initialAnalysis,
        history: contextRef.current.history
      });

      if (response.error) {
        throw new Error(response.error);
      }

      const responseData = response.data as any;
      const assistantMessage: ChatMessage = {
        role: 'model',
        text: responseData?.text || responseData?.message || 'No response',
        timestamp: Date.now()
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Update context history
      contextRef.current.history = [...contextRef.current.history, newMsg, assistantMessage];
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { 
        role: 'model', 
        text: "Sorry, I encountered an error processing your request.", 
        timestamp: Date.now() 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`flex flex-col bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-lg h-full ${className}`}>
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent min-h-0">
        {messages.length === 0 && (
            <div className="text-center py-10 text-slate-500">
                <p>Ask follow-up questions about the chart...</p>
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                    {["Where should I place my stop loss?", "What is the price target?", "Explain the volume pattern"].map(suggestion => (
                        <button 
                            key={suggestion}
                            onClick={() => setInput(suggestion)}
                            className="text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 px-3 py-1 rounded-full transition-colors"
                        >
                            {suggestion}
                        </button>
                    ))}
                </div>
            </div>
        )}
        
        {messages.map((msg, idx) => {
            const isSystemUpdate = msg.text.startsWith('[SYSTEM UPDATE');
            if (isSystemUpdate) {
                return (
                    <div key={idx} className="flex justify-center my-4">
                        <div className="bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2 text-xs text-slate-400 italic max-w-[80%] text-center">
                            {msg.text.split('\n')[0]} (Context Updated)
                        </div>
                    </div>
                );
            }

            return (
            <div key={idx} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div 
                className={`
                    max-w-[95%] sm:max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed relative group break-words
                    ${msg.role === 'user' 
                    ? 'bg-emerald-600 text-white rounded-br-none whitespace-pre-wrap' 
                    : 'bg-slate-800 text-slate-200 rounded-bl-none border border-slate-700'}
                `}
                >
                {msg.role === 'user' ? (
                  msg.text
                ) : (
                  <div className="prose prose-sm prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0.5">
                    <MarkdownRenderer content={msg.text} />
                  </div>
                )}
                </div>
                {msg.timestamp && (
                    <span className="text-[10px] text-slate-500 mt-1 px-1">
                        {formatTime(msg.timestamp)}
                    </span>
                )}
            </div>
            );
        })}
        
        {isLoading && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
             <div className="flex justify-start">
                <div className="bg-slate-800 rounded-2xl rounded-bl-none px-4 py-3 border border-slate-700 flex items-center space-x-1">
                    <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
             </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <form onSubmit={handleSend} className="p-4 bg-slate-900 border-t border-slate-800">
        {/* Re-Analyze Button - shown when callback is provided */}
        {onReAnalyze && (
          <button
            type="button"
            onClick={onReAnalyze}
            disabled={isReAnalyzing}
            className="w-full mb-3 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600/20 to-blue-600/20 hover:from-purple-600/30 hover:to-blue-600/30 border border-purple-500/30 text-purple-300 px-4 py-2.5 rounded-xl text-sm font-medium transition-all disabled:opacity-50"
          >
            {isReAnalyzing ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Re-analyzing chart...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Re-Analyze Chart with Guru</span>
              </>
            )}
          </button>
        )}
        <div className="relative">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a follow-up question..."
            disabled={isLoading}
            className="w-full bg-slate-950 border border-slate-700 rounded-xl pl-4 pr-12 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-emerald-500 hover:bg-emerald-400 text-white rounded-lg disabled:opacity-50 disabled:hover:bg-emerald-500 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatInterface;